import React, { useEffect, useState, useRef } from 'react';
import { Layers, DollarSign, AlertTriangle, TrendingUp, Clock, Activity, Zap, ChevronRight, ChevronDown, ChevronUp, Timer, UserCheck, ArrowUp, ArrowDown, Play, Pause, Eye, Trash2, GripVertical, Crown, Calendar, CreditCard, Info, CheckCircle2, ArrowRight, LayoutList, Target, ExternalLink, Database, Server, ShieldCheck, History, Receipt, AlertCircle, RefreshCw, Lock, Unlock, Smartphone, Mail, Settings, X, Monitor } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface AdQueueManagerProps {
  placements: any[];
  slotsData: any[];
  setDetailPanel: (panel: any) => void;
  setShowDeleteConfirm: (id: string) => void;
  moveQueueItem: (id: string, direction: number) => void;
  activateQueueItem: (id: string) => void;
  togglePlacementStatus: (id: string, status: string) => void;
  toggleFastTrack: (id: string) => void;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(amount);
};
// --- MOCK AUDIT LOGS ---
const initialLogs = [{
  id: 'LOG-1',
  time: '10:42:15',
  type: 'auto_swap',
  message: 'Авто-замена: PLC-101 завершен (лимит показов). Активирован PLC-102.',
  status: 'success'
}, {
  id: 'LOG-2',
  time: '10:41:02',
  type: 'payment',
  message: 'Оплата подтверждена для PLC-105. Статус изменен на "В очереди".',
  status: 'success'
}, {
  id: 'LOG-3',
  time: '10:35:18',
  type: 'conflict',
  message: 'Обнаружен конфликт дат для PLC-108 (Слот занят VIP-размещением).',
  status: 'warning'
}, {
  id: 'LOG-4',
  time: '10:30:00',
  type: 'notification',
  message: 'Отправлено 14 SMS-уведомлений о скором старте.',
  status: 'info'
}, {
  id: 'LOG-5',
  time: '10:15:22',
  type: 'lock',
  message: 'Slot Engine: Блокировка слота SLOT-04 снята (race condition prevented).',
  status: 'info'
}];
export function AdQueueManager({
  placements,
  slotsData,
  setDetailPanel,
  setShowDeleteConfirm,
  moveQueueItem,
  activateQueueItem,
  togglePlacementStatus,
  toggleFastTrack
}: AdQueueManagerProps) {
  // --- STATE ---
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [engineStatus, setEngineStatus] = useState<'active' | 'degraded' | 'error' | 'maintenance'>('active');
  const [logs, setLogs] = useState(initialLogs);
  const [activeModal, setActiveModal] = useState<'logs' | 'payment' | 'conflict' | 'timing' | null>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [slotLocks, setSlotLocks] = useState<Record<string, boolean>>({});
  const [simulatedTime, setSimulatedTime] = useState(new Date());
  const [selectedPipelineSlot, setSelectedPipelineSlot] = useState<string>('all');
  // Local queue state for simulation
  const [localPlacements, setLocalPlacements] = useState(placements);
  // Sync with props
  useEffect(() => {
    setLocalPlacements(placements);
  }, [placements]);
  // --- SIMULATION ENGINE ---
  useEffect(() => {
    const timer = setInterval(() => {
      setSimulatedTime(new Date());
      // Simulate high-load engine ticks
      if (Math.random() > 0.95) {
        addLog('system', 'Redis cache sync completed (12ms)', 'info');
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const addLog = (type: string, message: string, status: string) => {
    setLogs(prev => [{
      id: `LOG-${Date.now()}`,
      time: new Date().toLocaleTimeString('ru-RU'),
      type,
      message,
      status
    }, ...prev].slice(0, 50));
  };
  // --- HANDLERS ---
  const toggleExpand = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) newExpanded.delete(id);else newExpanded.add(id);
    setExpandedItems(newExpanded);
  };
  const handleAction = (action: string, item: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    switch (action) {
      case 'view':
        setDetailPanel({
          type: 'queue-item',
          title: 'Очередь: ' + item.object,
          data: item
        });
        break;
      case 'payment':
        setSelectedItem(item);
        setActiveModal('payment');
        break;
      case 'conflict':
        setSelectedItem(item);
        setActiveModal('conflict');
        break;
      case 'timing':
        setSelectedItem(item);
        setActiveModal('timing');
        break;
      case 'activate':
        if (item.payment !== 'paid' && item.paymentStatus !== 'paid') {
          addLog('error', `Отказ активации ${item.id}: Требуется оплата`, 'error');
          alert('Невозможно активировать: требуется оплата');
          return;
        }
        // Simulate lock
        setSlotLocks(prev => ({
          ...prev,
          [item.slotName]: true
        }));
        addLog('lock', `Слот ${item.slotName} заблокирован для транзакции`, 'info');
        setTimeout(() => {
          activateQueueItem(item.id);
          setSlotLocks(prev => ({
            ...prev,
            [item.slotName]: false
          }));
          addLog('activation', `Размещение ${item.id} успешно активировано`, 'success');
        }, 800);
        break;
      case 'pause':
        togglePlacementStatus(item.id, 'paused');
        addLog('pause', `Размещение ${item.id} поставлено на паузу вручную`, 'warning');
        break;
      case 'fastTrack':
        toggleFastTrack(item.id);
        addLog('fast_track', `Fast Track ${item.fastTrack ? 'отключен' : 'включен'} для ${item.id}`, 'info');
        break;
      case 'delete':
        setShowDeleteConfirm(item.id);
        break;
    }
  };
  // --- DERIVED DATA ---
  const queuedItems = localPlacements.filter(p => p.status === 'queued' || p.status === 'paused').sort((a, b) => {
    // Fast track first, then priority, then position
    if (a.fastTrack && !b.fastTrack) return -1;
    if (!a.fastTrack && b.fastTrack) return 1;
    if (b.priority !== a.priority) return b.priority - a.priority;
    return (a.queuePosition || 0) - (b.queuePosition || 0);
  });
  const activeItems = localPlacements.filter(p => p.status === 'active');
  // --- RENDERERS ---
  const renderEngineStatus = () => <Card padding="sm" className="bg-slate-900 border-slate-800 text-white mb-6 shadow-xl" data-id="element-8197">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-2" data-id="element-8198">
        <div className="flex items-center gap-6" data-id="element-8199">
          <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setActiveModal('logs')} data-id="element-8200">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${engineStatus === 'active' ? 'bg-emerald-500/20 text-emerald-400' : engineStatus === 'degraded' ? 'bg-amber-500/20 text-amber-400' : 'bg-rose-500/20 text-rose-400'}`} data-id="element-8201">
              <Server className="w-5 h-5" data-id="element-8202" />
            </div>
            <div data-id="element-8203">
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-0.5" data-id="element-8204">
                Engine Status
              </div>
              <div className="flex items-center gap-2" data-id="element-8205">
                <span className="font-bold text-lg" data-id="element-8206">
                  {engineStatus === 'active' ? 'ACTIVE & HEALTHY' : engineStatus.toUpperCase()}
                </span>
                <div className={`w-2 h-2 rounded-full animate-pulse ${engineStatus === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`} data-id="element-8207" />
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-6 pl-6 border-l border-slate-700" data-id="element-8208">
            <div className="flex flex-col cursor-help" title="Redis Cache Status: Connected" data-id="element-8209">
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-8210">
                Cache (Redis)
              </span>
              <span className="text-sm font-medium text-emerald-400 flex items-center gap-1" data-id="element-8211">
                <Database className="w-3.5 h-3.5" data-id="element-8212" /> HIT 99.8%
              </span>
            </div>
            <div className="flex flex-col cursor-help" title="Async Queue Workers: 12 Active" data-id="element-8213">
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-8214">
                Workers
              </span>
              <span className="text-sm font-medium text-blue-400 flex items-center gap-1" data-id="element-8215">
                <Activity className="w-3.5 h-3.5" data-id="element-8216" /> 12 Active
              </span>
            </div>
            <div className="flex flex-col cursor-help" title="Average Response Time" data-id="element-8217">
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-8218">
                Response
              </span>
              <span className="text-sm font-medium text-emerald-400 flex items-center gap-1" data-id="element-8219">
                <Zap className="w-3.5 h-3.5" data-id="element-8220" /> 42ms
              </span>
            </div>
            <div className="flex flex-col cursor-help" title="Notification Service" data-id="element-8221">
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-8222">
                SMS / Email
              </span>
              <span className="text-sm font-medium text-indigo-400 flex items-center gap-1" data-id="element-8223">
                <Mail className="w-3.5 h-3.5" data-id="element-8224" /> 142 sent today
              </span>
            </div>
          </div>
        </div>

        <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white shrink-0" icon={<History className="w-4 h-4" data-id="element-8226" />} onClick={() => setActiveModal('logs')} data-id="element-8225">
          Audit Logs
        </Button>
      </div>
    </Card>;
  const renderAutoLogicPipeline = () => {
    // Get unique slot names from placements + slotsData
    const allSlotNames = Array.from(new Set([...slotsData.map(s => s.name), ...localPlacements.map(p => p.slotName).filter(Boolean)]));
    // Filter by selected slot
    const filteredActive = selectedPipelineSlot === 'all' ? activeItems : activeItems.filter(p => p.slotName === selectedPipelineSlot);
    const filteredQueued = selectedPipelineSlot === 'all' ? queuedItems : queuedItems.filter(p => p.slotName === selectedPipelineSlot);
    const selectedSlotData = slotsData.find(s => s.name === selectedPipelineSlot);
    const isLocked = selectedPipelineSlot === 'all' ? Object.values(slotLocks).some(v => v) : slotLocks[selectedPipelineSlot];
    // For "all" mode, show summary; for specific slot, show pipeline
    const active = filteredActive[0] || null;
    const next1 = filteredQueued[0];
    const next2 = filteredQueued[1];
    const next3 = filteredQueued[2];
    return <Card padding="none" className="mb-6 rounded-xl shadow-sm border-slate-200 overflow-hidden bg-slate-50/50" data-id="element-8227">
        {/* Header with slot selector */}
        <div className="p-4 border-b border-slate-200 bg-white" data-id="element-8228">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3" data-id="element-8229">
            <div className="flex items-center gap-3" data-id="element-8230">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-8231">
                <RefreshCw className="w-4 h-4 text-indigo-600" data-id="element-8232" />
              </div>
              <div data-id="element-8233">
                <h3 className="text-sm font-bold text-slate-900" data-id="element-8234">
                  Автоматическая ротация (Pipeline)
                </h3>
                <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-8235">
                  {selectedPipelineSlot === 'all' ? `Обзор всех ${allSlotNames.length} слотов` : `Движок для слота: ${selectedPipelineSlot}`}
                </p>
              </div>
            </div>
            <Badge variant={isLocked ? 'warning' : 'success'} className="animate-pulse flex items-center gap-1" data-id="element-8236">
              {isLocked ? <Lock className="w-3 h-3" data-id="element-8237" /> : <Unlock className="w-3 h-3" data-id="element-8238" />}
              {isLocked ? 'SLOT LOCKED' : 'SLOT READY'}
            </Badge>
          </div>

          {/* Slot selector tabs */}
          <div className="flex flex-wrap gap-1.5" data-id="element-8239">
            <button className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedPipelineSlot === 'all' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} onClick={() => setSelectedPipelineSlot('all')} data-id="element-8240">
              <span className="flex items-center gap-1.5" data-id="element-8241">
                <Monitor className="w-3 h-3" data-id="element-8242" />
                Все слоты
              </span>
            </button>
            {allSlotNames.map(slotName => {
            const slotInfo = slotsData.find(s => s.name === slotName);
            const slotActive = activeItems.filter(p => p.slotName === slotName).length;
            const slotQueued = queuedItems.filter(p => p.slotName === slotName).length;
            const hasConflict = queuedItems.some(p => p.slotName === slotName && p.conflict);
            return <button key={slotName} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${selectedPipelineSlot === slotName ? 'bg-indigo-600 text-white shadow-sm' : hasConflict ? 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} onClick={() => setSelectedPipelineSlot(slotName)} data-id="element-8243">
                  {slotName}
                  {slotActive > 0 && <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${selectedPipelineSlot === slotName ? 'bg-white/20' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-8244">
                      {slotActive}
                    </span>}
                  {slotQueued > 0 && <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${selectedPipelineSlot === slotName ? 'bg-white/20' : 'bg-amber-100 text-amber-700'}`} data-id="element-8245">
                      +{slotQueued}
                    </span>}
                </button>;
          })}
          </div>
        </div>

        {/* Pipeline content */}
        {selectedPipelineSlot === 'all' /* ALL SLOTS OVERVIEW */ ? <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-8246">
            {allSlotNames.map(slotName => {
          const slotInfo = slotsData.find(s => s.name === slotName);
          const slotActiveItems = activeItems.filter(p => p.slotName === slotName);
          const slotQueuedItems = queuedItems.filter(p => p.slotName === slotName);
          const currentActive = slotActiveItems[0];
          const nextUp = slotQueuedItems[0];
          const locked = slotLocks[slotName];
          return <div key={slotName} className="bg-white p-4 rounded-xl border border-slate-200 hover:border-indigo-300 cursor-pointer transition-all hover:shadow-sm" onClick={() => setSelectedPipelineSlot(slotName)} data-id="element-8247">
                  <div className="flex justify-between items-start mb-3" data-id="element-8248">
                    <div data-id="element-8249">
                      <div className="text-xs font-bold text-slate-900 mb-0.5" data-id="element-8250">
                        {slotName}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-8251">
                        {slotInfo?.page} • {slotInfo?.type} • {slotInfo?.size}
                      </div>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-8252">
                      {locked && <Lock className="w-3 h-3 text-amber-500" data-id="element-8253" />}
                      <div className={`w-2 h-2 rounded-full ${currentActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} data-id="element-8254" />
                    </div>
                  </div>

                  {currentActive ? <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 mb-2" data-id="element-8255">
                      <div className="flex items-center gap-1.5 text-[10px] text-emerald-700 font-bold mb-1" data-id="element-8256">
                        <Play className="w-3 h-3 fill-current" data-id="element-8257" /> Транслируется
                      </div>
                      <div className="text-xs font-bold text-slate-900 truncate" data-id="element-8258">
                        {currentActive.object}
                      </div>
                      <div className="flex items-center gap-2 mt-1" data-id="element-8259">
                        <div className="flex-1 bg-emerald-100 rounded-full h-1" data-id="element-8260">
                          <div className="bg-emerald-500 h-1 rounded-full" style={{
                    width: `${Math.min(100, currentActive.impressionsFact / currentActive.impressionsPlan * 100)}%`
                  }} data-id="element-8261" />
                        </div>
                        <span className="text-[9px] text-emerald-600 font-medium" data-id="element-8262">
                          {Math.round(currentActive.impressionsFact / currentActive.impressionsPlan * 100)}
                          %
                        </span>
                      </div>
                    </div> : <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-2.5 mb-2 text-center" data-id="element-8263">
                      <span className="text-[10px] text-slate-400 font-medium" data-id="element-8264">
                        Нет активных
                      </span>
                    </div>}

                  {nextUp ? <div className="flex items-center gap-2 text-[10px] text-slate-600" data-id="element-8265">
                      <ArrowRight className="w-3 h-3 text-indigo-400" data-id="element-8266" />
                      <span data-id="element-8267">
                        Следующий:{' '}
                        <strong className="text-slate-900" data-id="element-8268">
                          {nextUp.object}
                        </strong>
                      </span>
                      {nextUp.fastTrack && <Zap className="w-3 h-3 text-amber-500" data-id="element-8269" />}
                    </div> : <div className="flex items-center gap-2 text-[10px] text-slate-400" data-id="element-8270">
                      <ArrowRight className="w-3 h-3" data-id="element-8271" />
                      <span data-id="element-8272">
                        Очередь пуста • Fallback: {slotInfo?.fallback || 'hide'}
                      </span>
                    </div>}

                  {slotQueuedItems.length > 1 && <div className="mt-1.5 text-[9px] text-slate-400" data-id="element-8273">
                      + ещё {slotQueuedItems.length - 1} в очереди
                    </div>}
                </div>;
        })}
          </div> /* SINGLE SLOT PIPELINE */ : <div className="p-6 overflow-x-auto hide-scrollbar" data-id="element-8274">
            {/* Slot info bar */}
            {selectedSlotData && <div className="flex flex-wrap items-center gap-3 mb-4 pb-3 border-b border-slate-100" data-id="element-8275">
                <div className="flex items-center gap-2 text-[10px] text-slate-500" data-id="element-8276">
                  <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-8277" />
                  <span className="font-bold text-slate-700" data-id="element-8278">
                    {selectedSlotData.page}
                  </span>
                  <span data-id="element-8279">•</span>
                  <span data-id="element-8280">{selectedSlotData.type}</span>
                  <span data-id="element-8281">•</span>
                  <span data-id="element-8282">{selectedSlotData.size}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]" data-id="element-8283">
                  <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-8284">
                    Лимит: {selectedSlotData.limit}
                  </Badge>
                  <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-8285">
                    Ротация: {selectedSlotData.rotation}
                  </Badge>
                  <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-8286">
                    Fallback: {selectedSlotData.fallback}
                  </Badge>
                </div>
              </div>}

            <div className="flex items-center min-w-[800px] gap-4" data-id="element-8287">
              {/* ACTIVE */}
              {active ? <div className="flex-1 bg-white p-4 rounded-xl border-2 border-emerald-400 shadow-[0_0_20px_rgba(52,211,153,0.15)] relative cursor-pointer hover:bg-emerald-50/30 transition-colors" onClick={() => handleAction('view', active)} data-id="element-8288">
                  <div className="absolute -top-3 left-4 bg-emerald-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-8289">
                    Транслируется
                  </div>
                  <div className="flex justify-between items-start mt-2 mb-2" data-id="element-8290">
                    <div className="font-bold text-slate-900" data-id="element-8291">
                      {active.object}
                    </div>
                    <div className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600" data-id="element-8292">
                      {active.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3" data-id="element-8293">
                    <Timer className="w-3.5 h-3.5 text-emerald-500" data-id="element-8294" />
                    Осталось:{' '}
                    <span className="font-bold text-slate-900" data-id="element-8295">
                      {active.timer || active.daysLeft + ' дн.'}
                    </span>
                  </div>
                  <div className="space-y-1" data-id="element-8296">
                    <div className="flex justify-between text-[10px] text-slate-500" data-id="element-8297">
                      <span data-id="element-8298">Лимит показов</span>
                      <span data-id="element-8299">
                        {active.impressionsFact?.toLocaleString()} /{' '}
                        {active.impressionsPlan?.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-8300">
                      <div className="bg-emerald-400 h-1.5 rounded-full transition-all" style={{
                  width: `${Math.min(100, (active.impressionsFact || 0) / (active.impressionsPlan || 1) * 100)}%`
                }} data-id="element-8301" />
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-500 flex items-center gap-1" data-id="element-8302">
                    <UserCheck className="w-3 h-3" data-id="element-8303" /> {active.advertiser}
                  </div>
                </div> : <div className="flex-1 bg-slate-50 p-4 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 min-h-[160px]" data-id="element-8304">
                  <Layers className="w-6 h-6 mb-2 opacity-50" data-id="element-8305" />
                  <span className="text-xs font-medium" data-id="element-8306">
                    Нет активных размещений
                  </span>
                  <span className="text-[10px] mt-1" data-id="element-8307">
                    Fallback: {selectedSlotData?.fallback || 'default banner'}
                  </span>
                </div>}

              {/* ARROW */}
              <div className="flex flex-col items-center justify-center px-2" data-id="element-8308">
                <div className="text-[9px] font-bold text-indigo-600 uppercase tracking-wider mb-1 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100" data-id="element-8309">
                  onExpire()
                </div>
                <div className="flex items-center w-16" data-id="element-8310">
                  <div className="h-0.5 bg-indigo-200 flex-1" data-id="element-8311" />
                  <ArrowRight className="w-4 h-4 text-indigo-400" data-id="element-8312" />
                </div>
              </div>

              {/* NEXT 1 */}
              {next1 ? <div className="flex-1 bg-white p-4 rounded-xl border-2 border-indigo-200 shadow-sm relative cursor-pointer hover:border-indigo-400 transition-colors" onClick={() => handleAction('view', next1)} data-id="element-8313">
                  <div className="absolute -top-3 left-4 bg-indigo-100 text-indigo-700 border border-indigo-200 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-8314">
                    Следующий (#1)
                  </div>
                  <div className="flex justify-between items-start mt-2 mb-2" data-id="element-8315">
                    <div className="font-bold text-slate-900" data-id="element-8316">
                      {next1.object}
                    </div>
                    <div className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600" data-id="element-8317">
                      {next1.id}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 mb-3" data-id="element-8318">
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-600" data-id="element-8319">
                      <Calendar className="w-3 h-3 text-indigo-400" data-id="element-8320" />
                      Старт:{' '}
                      <span className="font-bold text-slate-900" data-id="element-8321">
                        {next1.slotFreeDate || 'Сразу после освобождения'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-600" data-id="element-8322">
                      <Info className="w-3 h-3 text-amber-500" data-id="element-8323" />
                      Причина:{' '}
                      <span className="font-bold text-amber-700 bg-amber-50 px-1 rounded" data-id="element-8324">
                        {next1.fastTrack ? 'Fast Track' : next1.priority >= 8 ? 'Highest Priority' : 'Earliest Booking'}
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-100 flex justify-between items-center" data-id="element-8325">
                    <Badge variant={next1.payment === 'paid' ? 'success' : 'warning'} size="sm" className="text-[9px]" data-id="element-8326">
                      {next1.payment === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={e => handleAction('activate', next1, e)} data-id="element-8327">
                      Активировать
                    </Button>
                  </div>
                </div> : <div className="flex-1 bg-slate-50 p-4 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 min-h-[140px]" data-id="element-8328">
                  <Layers className="w-6 h-6 mb-2 opacity-50" data-id="element-8329" />
                  <span className="text-xs font-medium" data-id="element-8330">Очередь пуста</span>
                  <span className="text-[10px] mt-1" data-id="element-8331">
                    Fallback: {selectedSlotData?.fallback || 'default banner'}
                  </span>
                </div>}

              {/* ARROW */}
              <div className="flex items-center w-8" data-id="element-8332">
                <div className="h-0.5 bg-slate-200 flex-1" data-id="element-8333" />
                <ArrowRight className="w-4 h-4 text-slate-300" data-id="element-8334" />
              </div>

              {/* NEXT 2 & 3 */}
              <div className="w-48 shrink-0 flex flex-col gap-2" data-id="element-8335">
                {next2 ? <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm opacity-80 cursor-pointer hover:opacity-100 transition-opacity" onClick={() => handleAction('view', next2)} data-id="element-8336">
                    <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-8337">
                      В очереди (#2)
                    </div>
                    <div className="font-bold text-slate-800 text-sm truncate" data-id="element-8338">
                      {next2.object}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1" data-id="element-8339">
                      <Clock className="w-3 h-3" data-id="element-8340" /> Ожидание ~
                      {next2.daysLeft || '?'}д
                    </div>
                  </div> : <div className="bg-slate-50 p-3 rounded-xl border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 min-h-[60px]" data-id="element-8341">
                    <span className="text-[10px] font-medium" data-id="element-8342">Пусто (#2)</span>
                  </div>}
                {next3 ? <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm opacity-60 cursor-pointer hover:opacity-100 transition-opacity" onClick={() => handleAction('view', next3)} data-id="element-8343">
                    <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-8344">
                      В очереди (#3)
                    </div>
                    <div className="font-bold text-slate-800 text-sm truncate" data-id="element-8345">
                      {next3.object}
                    </div>
                  </div> : <div className="bg-slate-50 p-2.5 rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-slate-400" data-id="element-8346">
                    <span className="text-[9px] font-medium" data-id="element-8347">Fallback</span>
                  </div>}
              </div>
            </div>
          </div>}
      </Card>;
  };
  const renderQueueTable = () => {
    return <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-8348">
        <div className="p-4 border-b border-slate-200 bg-white flex justify-between items-center" data-id="element-8349">
          <h3 className="text-sm font-bold text-slate-900" data-id="element-8350">
            Детальная очередь
          </h3>
          <div className="flex gap-2" data-id="element-8351">
            <Badge variant="neutral" size="sm" data-id="element-8352">
              Всего: {queuedItems.length}
            </Badge>
            <Badge variant="error" size="sm" data-id="element-8353">
              Конфликтов: {queuedItems.filter(q => q.conflict).length}
            </Badge>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-4 px-4 py-3 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-200" data-id="element-8354">
          <div className="w-12 shrink-0 text-center" data-id="element-8355">Поз.</div>
          <div className="flex-1 min-w-[200px]" data-id="element-8356">ID / Объект</div>
          <div className="w-[160px] shrink-0" data-id="element-8357">Слот / Приоритет</div>
          <div className="w-[140px] shrink-0" data-id="element-8358">Оплата / Статус</div>
          <div className="w-[180px] shrink-0" data-id="element-8359">Тайминг</div>
          <div className="w-[180px] shrink-0 text-right" data-id="element-8360">Действия</div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-8361">
          {queuedItems.map((q, index) => {
          const isConflict = q.conflict;
          const isPaid = q.payment === 'paid' || q.paymentStatus === 'paid';
          return <div key={q.id} className={`flex flex-col lg:flex-row lg:items-center gap-4 p-4 transition-colors ${isConflict ? 'bg-rose-50/30' : 'bg-white hover:bg-slate-50'}`} data-id="element-8362">
                {/* Position */}
                <div className="w-12 shrink-0 items-center justify-center gap-1 hidden lg:flex" data-id="element-8363">
                  <GripVertical className="w-3 h-3 text-slate-300 cursor-grab hover:text-slate-500" data-id="element-8364" />
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-slate-50 text-slate-500 border-slate-200'}`} data-id="element-8365">
                    #{index + 1}
                  </div>
                </div>

                {/* Identity */}
                <div className="flex-1 min-w-[200px]" data-id="element-8366">
                  <div className="flex items-center gap-2 mb-1" data-id="element-8367">
                    <div className="text-[10px] font-bold text-indigo-600 font-mono bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:underline" onClick={() => handleAction('view', q)} data-id="element-8368">
                      {q.id}
                    </div>
                    {q.fastTrack && <Badge variant="warning" size="sm" className="text-[9px] py-0" data-id="element-8369">
                        <Zap className="w-2.5 h-2.5 mr-0.5" data-id="element-8370" /> Fast Track
                      </Badge>}
                  </div>
                  <div className="text-sm font-bold text-slate-900 mb-0.5 cursor-pointer hover:text-indigo-600 flex items-center gap-1" onClick={() => handleAction('view', q)} data-id="element-8371">
                    {q.object} <ExternalLink className="w-3 h-3 opacity-50" data-id="element-8372" />
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1" data-id="element-8373">
                    <UserCheck className="w-3 h-3" data-id="element-8374" /> {q.advertiser}
                  </div>
                </div>

                {/* Slot & Priority */}
                <div className="w-full lg:w-[160px] shrink-0" data-id="element-8375">
                  <div className="text-xs font-bold text-slate-900 mb-1.5 flex items-center gap-1.5" data-id="element-8376">
                    <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-8377" />
                    {q.slotName}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-8378">
                    <Badge variant={q.priority >= 8 ? 'error' : q.priority >= 5 ? 'warning' : 'neutral'} size="sm" className="text-[9px]" data-id="element-8379">
                      {q.priority >= 8 ? <Crown className="w-2.5 h-2.5 mr-1" data-id="element-8380" /> : null}
                      Приоритет: {q.priority}/10
                    </Badge>
                  </div>
                </div>

                {/* Payment & Status */}
                <div className="w-full lg:w-[140px] shrink-0 flex flex-col gap-1.5" data-id="element-8381">
                  <div className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded cursor-pointer transition-colors ${isPaid ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`} onClick={() => handleAction('payment', q)} title="Нажмите для просмотра деталей оплаты" data-id="element-8382">
                    <Receipt className="w-3 h-3" data-id="element-8383" />
                    {isPaid ? 'Оплачено' : 'Ожидает оплаты'}
                  </div>

                  <div className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded cursor-help" title={q.statusReason === 'slot_full' ? 'Слот занят другим размещением' : q.statusReason === 'payment_pending' ? 'Ожидается подтверждение оплаты' : q.statusReason === 'date_conflict' ? 'Конфликт дат с другим размещением' : q.statusReason === 'waiting_start' ? 'Ожидание наступления даты старта' : q.statusReason === 'manual_pause' ? 'Поставлено на паузу вручную' : q.statusReason} data-id="element-8384">
                    <Info className="w-3 h-3 text-slate-400" data-id="element-8385" />
                    {q.statusReason === 'slot_full' ? 'Слот занят' : q.statusReason}
                  </div>
                </div>

                {/* Timing & Conflicts */}
                <div className="w-full lg:w-[180px] shrink-0 flex flex-col gap-1.5" data-id="element-8386">
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-700 cursor-pointer hover:text-indigo-600" onClick={() => handleAction('timing', q)} data-id="element-8387">
                    <Calendar className="w-3.5 h-3.5 text-indigo-400" data-id="element-8388" />
                    <span className="font-medium" data-id="element-8389">
                      Старт: {q.slotFreeDate || 'Сразу'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500" data-id="element-8390">
                    <Clock className="w-3.5 h-3.5" data-id="element-8391" />
                    Ожидание: ~{q.daysLeft || 2} дн.
                  </div>

                  {isConflict && <div className="flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100 cursor-pointer hover:bg-rose-100 mt-1" onClick={() => handleAction('conflict', q)} data-id="element-8392">
                      <AlertTriangle className="w-3 h-3" data-id="element-8393" /> Конфликт дат
                    </div>}
                </div>

                {/* Actions */}
                <div className="w-full lg:w-[180px] shrink-0 flex flex-wrap items-center justify-end gap-1 mt-2 lg:mt-0" data-id="element-8394">
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Поднять" onClick={() => moveQueueItem(q.id, -1)} data-id="element-8395">
                    <ArrowUp className="w-4 h-4" data-id="element-8396" />
                  </Button>
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Опустить" onClick={() => moveQueueItem(q.id, 1)} data-id="element-8397">
                    <ArrowDown className="w-4 h-4" data-id="element-8398" />
                  </Button>
                  <Button variant="ghost" size="sm" className={`w-8 h-8 p-0 ${q.fastTrack ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:text-amber-500'}`} title="Fast Track" onClick={() => handleAction('fastTrack', q)} data-id="element-8399">
                    <Zap className="w-4 h-4" data-id="element-8400" />
                  </Button>
                  <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-8401" />
                  {q.status === 'paused' ? <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-emerald-500 hover:bg-emerald-50" title="Возобновить" onClick={() => togglePlacementStatus(q.id, 'queued')} data-id="element-8402">
                      <Play className="w-4 h-4" data-id="element-8403" />
                    </Button> : <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-amber-500 hover:bg-amber-50" title="Пауза" onClick={() => handleAction('pause', q)} data-id="element-8404">
                      <Pause className="w-4 h-4" data-id="element-8405" />
                    </Button>}
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-indigo-600 hover:bg-indigo-50" title="Принудительный старт" onClick={e => handleAction('activate', q, e)} data-id="element-8406">
                    <Play className="w-4 h-4 fill-current" data-id="element-8407" />
                  </Button>
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-rose-400 hover:text-rose-600 hover:bg-rose-50" title="Удалить" onClick={() => handleAction('delete', q)} data-id="element-8408">
                    <Trash2 className="w-4 h-4" data-id="element-8409" />
                  </Button>
                </div>
              </div>;
        })}

          {queuedItems.length === 0 && <div className="p-12 text-center flex flex-col items-center justify-center text-slate-500" data-id="element-8410">
              <Layers className="w-12 h-12 mb-4 text-slate-300" data-id="element-8411" />
              <h4 className="text-lg font-bold text-slate-900 mb-1" data-id="element-8412">
                Очередь пуста
              </h4>
              <p className="text-sm" data-id="element-8413">
                Все рекламные размещения активны или завершены.
              </p>
            </div>}
        </div>
      </Card>;
  };
  const renderModals = () => {
    if (!activeModal) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setActiveModal(null);
    }} data-id="element-8414">
        {/* LOGS MODAL */}
        {activeModal === 'logs' && <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-8415">
            <div className="p-4 border-b border-slate-200 bg-slate-900 text-white flex justify-between items-center shrink-0" data-id="element-8416">
              <div className="flex items-center gap-3" data-id="element-8417">
                <Server className="w-5 h-5 text-indigo-400" data-id="element-8418" />
                <div data-id="element-8419">
                  <h3 className="font-bold" data-id="element-8420">Системные логи Engine</h3>
                  <p className="text-[10px] text-slate-400" data-id="element-8421">
                    Аудит автоматических действий и ошибок
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" icon={<X className="w-5 h-5" data-id="element-8423" />} onClick={() => setActiveModal(null)} data-id="element-8422" />
            </div>
            <div className="p-0 overflow-y-auto bg-slate-950 flex-1 font-mono text-xs" data-id="element-8424">
              {logs.map(log => <div key={log.id} className="flex gap-4 p-3 border-b border-slate-800 hover:bg-slate-900/50" data-id="element-8425">
                  <div className="text-slate-500 shrink-0" data-id="element-8426">{log.time}</div>
                  <div className={`shrink-0 w-24 ${log.status === 'error' ? 'text-rose-400' : log.status === 'warning' ? 'text-amber-400' : log.status === 'success' ? 'text-emerald-400' : 'text-blue-400'}`} data-id="element-8427">
                    [{log.type.toUpperCase()}]
                  </div>
                  <div className="text-slate-300" data-id="element-8428">{log.message}</div>
                </div>)}
            </div>
          </Card>}

        {/* PAYMENT MODAL */}
        {activeModal === 'payment' && selectedItem && <Card className="w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-8429">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-8430">
              <div className="flex items-center gap-2" data-id="element-8431">
                <Receipt className="w-5 h-5 text-indigo-600" data-id="element-8432" />
                <h3 className="font-bold text-slate-900" data-id="element-8433">Детали оплаты</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-8435" />} onClick={() => setActiveModal(null)} data-id="element-8434" />
            </div>
            <div className="p-6 space-y-6" data-id="element-8436">
              <div className="text-center" data-id="element-8437">
                <div className="text-sm text-slate-500 mb-1" data-id="element-8438">
                  Сумма к оплате
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-8439">
                  {formatCurrency(selectedItem.price || 0)}
                </div>
                <Badge variant={selectedItem.payment === 'paid' ? 'success' : 'warning'} className="mt-2" data-id="element-8440">
                  {selectedItem.payment === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm" data-id="element-8441">
                <div className="flex justify-between" data-id="element-8442">
                  <span className="text-slate-500" data-id="element-8443">ID Размещения</span>
                  <span className="font-bold text-slate-900" data-id="element-8444">
                    {selectedItem.id}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-8445">
                  <span className="text-slate-500" data-id="element-8446">Инвойс</span>
                  <span className="font-mono text-indigo-600" data-id="element-8447">
                    INV-{selectedItem.id.split('-')[1]}-2026
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-8448">
                  <span className="text-slate-500" data-id="element-8449">Рекламодатель</span>
                  <span className="font-medium text-slate-900" data-id="element-8450">
                    {selectedItem.advertiser}
                  </span>
                </div>
              </div>

              {selectedItem.payment !== 'paid' && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-id="element-8451">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" data-id="element-8452" />
                  <div className="text-sm text-amber-800" data-id="element-8453">
                    <strong data-id="element-8454">Блокировка активации:</strong> Размещение не может
                    быть запущено до подтверждения оплаты. Система автоматически
                    пропустит его в очереди.
                  </div>
                </div>}

              <div className="flex gap-3 pt-4 border-t border-slate-100" data-id="element-8455">
                <Button variant="outline" className="flex-1" onClick={() => setActiveModal(null)} data-id="element-8456">
                  Закрыть
                </Button>
                {selectedItem.payment !== 'paid' && <Button variant="primary" className="flex-1" onClick={() => {
              addLog('payment', `Ручное подтверждение оплаты для ${selectedItem.id}`, 'success');
              setActiveModal(null);
              alert('Оплата подтверждена (симуляция)');
            }} data-id="element-8457">
                    Подтвердить оплату
                  </Button>}
              </div>
            </div>
          </Card>}

        {/* CONFLICT MODAL */}
        {activeModal === 'conflict' && selectedItem && <Card className="w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-8458">
            <div className="p-4 border-b border-rose-200 bg-rose-50 flex justify-between items-center" data-id="element-8459">
              <div className="flex items-center gap-2 text-rose-700" data-id="element-8460">
                <AlertCircle className="w-5 h-5" data-id="element-8461" />
                <h3 className="font-bold" data-id="element-8462">Разрешение конфликта</h3>
              </div>
              <Button variant="ghost" size="sm" className="text-rose-700 hover:bg-rose-100" icon={<X className="w-5 h-5" data-id="element-8464" />} onClick={() => setActiveModal(null)} data-id="element-8463" />
            </div>
            <div className="p-6 space-y-6" data-id="element-8465">
              <div className="text-sm text-slate-700" data-id="element-8466">
                Обнаружен конфликт для размещения{' '}
                <strong data-id="element-8467">
                  {selectedItem.id} ({selectedItem.object})
                </strong>
                .
              </div>

              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800" data-id="element-8468">
                <strong data-id="element-8469">Причина:</strong>{' '}
                {selectedItem.conflictReason || 'Слот занят VIP-размещением на выбранные даты. Невозможно гарантировать лимит показов.'}
              </div>

              <div className="space-y-3" data-id="element-8470">
                <h4 className="font-bold text-slate-900 text-sm" data-id="element-8471">
                  Варианты решения:
                </h4>

                <button className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex gap-3 group" onClick={() => {
              addLog('conflict_resolve', `Конфликт ${selectedItem.id} решен: смена дат`, 'success');
              setActiveModal(null);
            }} data-id="element-8472">
                  <Calendar className="w-5 h-5 text-indigo-500 shrink-0" data-id="element-8473" />
                  <div data-id="element-8474">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-700" data-id="element-8475">
                      Сдвинуть даты старта
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-8476">
                      Автоматически перенести старт на первую свободную дату
                      (ожидание +4 дня)
                    </div>
                  </div>
                </button>

                <button className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex gap-3 group" onClick={() => {
              addLog('conflict_resolve', `Конфликт ${selectedItem.id} решен: смена слота`, 'success');
              setActiveModal(null);
            }} data-id="element-8477">
                  <LayoutList className="w-5 h-5 text-indigo-500 shrink-0" data-id="element-8478" />
                  <div data-id="element-8479">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-700" data-id="element-8480">
                      Предложить другой слот
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-8481">
                      Перенести в слот "Боковой баннер" со скидкой 15%
                    </div>
                  </div>
                </button>

                <button className="w-full text-left p-4 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 transition-colors flex gap-3 group" onClick={() => {
              addLog('conflict_override', `Конфликт ${selectedItem.id} проигнорирован (Override)`, 'warning');
              setActiveModal(null);
            }} data-id="element-8482">
                  <ShieldCheck className="w-5 h-5 text-rose-600 shrink-0" data-id="element-8483" />
                  <div data-id="element-8484">
                    <div className="font-bold text-rose-900" data-id="element-8485">
                      Override (Принудительно)
                    </div>
                    <div className="text-xs text-rose-700 mt-1" data-id="element-8486">
                      Игнорировать конфликт. Возможна недокрутка показов у обоих
                      рекламодателей.
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </Card>}

        {/* TIMING MODAL */}
        {activeModal === 'timing' && selectedItem && <Card className="w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-8487">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-8488">
              <div className="flex items-center gap-2" data-id="element-8489">
                <Clock className="w-5 h-5 text-indigo-600" data-id="element-8490" />
                <h3 className="font-bold text-slate-900" data-id="element-8491">Детали тайминга</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-8493" />} onClick={() => setActiveModal(null)} data-id="element-8492" />
            </div>
            <div className="p-6 space-y-4 text-sm" data-id="element-8494">
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-8495">
                <span className="text-slate-500" data-id="element-8496">Тип старта</span>
                <Badge variant="neutral" data-id="element-8497">
                  {selectedItem.startType === 'immediate' ? 'По готовности' : 'Фикс. дата'}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-8498">
                <span className="text-slate-500" data-id="element-8499">Ожидаемый старт</span>
                <span className="font-bold text-slate-900" data-id="element-8500">
                  {selectedItem.slotFreeDate || 'Неизвестно'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-8501">
                <span className="text-slate-500" data-id="element-8502">Время ожидания</span>
                <span className="font-medium text-indigo-600" data-id="element-8503">
                  {selectedItem.daysLeft ? `~${selectedItem.daysLeft} дней` : '—'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-8504">
                <span className="text-slate-500" data-id="element-8505">Дедлайн старта</span>
                <span className="font-medium text-slate-900" data-id="element-8506">
                  {selectedItem.deadline || 'Нет'}
                </span>
              </div>

              <div className="mt-6 bg-blue-50 p-4 rounded-xl text-xs text-blue-800 flex gap-2" data-id="element-8507">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-8508" />
                <p data-id="element-8509">
                  Время старта рассчитывается динамически на основе текущей
                  скорости открутки показов активного баннера.
                </p>
              </div>
            </div>
          </Card>}
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-8510">
      {renderEngineStatus()}
      {renderAutoLogicPipeline()}
      {renderQueueTable()}
      {renderModals()}
    </div>;
}